import { SequentialDragger } from "./SequentialDragger";
import { trackShape } from "@/analytics/drawing-analytics";
import {
  DraggerType,
  type Point2D,
  type Point3D,
  type SegmentDelta,
} from "./types";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { AddEntityCommand } from "../commands/AddEntityCommand";
import { AddPointToPolylineCommand } from "../commands/AddPointToPolylineCommand";
import { ClosePolylineWithHatchCommand } from "../commands/ClosePolylineWithHatchCommand";
import {
  PREVIEW_COMPLETION_STYLE,
  PREVIEW_RUBBER_BAND_STYLE,
  PREVIEW_WILL_CLOSE_STYLE,
} from "./previewStyles";
import { MeasurementLabels } from "../overlay/MeasurementLabels";

/**
 * Dragger for drawing polylines (multi-point lines).
 * Tablet-first interaction model with incremental entity creation.
 *
 * Usage:
 * - Drag to position a point, lift stylus to fix it
 * - Repeat to add more points
 * - Escape to complete (open polyline)
 * - Release near start point (green circle) to close the polyline
 * - Ctrl+Z / Cmd+Z to undo last point
 * - Zoom/pan is supported - drawing state is preserved
 *
 * Entity creation:
 * - First point creates the polyline entity (AddEntityCommand)
 * - Each subsequent point is added via AddPointToPolylineCommand
 * - Standard undo works because each point is a command
 *
 * The preview shows:
 * - A dashed "rubber band" line from last fixed point to current drag position
 * - Green circle around start point when closeable (3+ points)
 */
/** Distance threshold in pixels for closing the shape */
const CLOSE_THRESHOLD = 25;

export class PolylineDragger extends SequentialDragger {
  readonly type = DraggerType.Polyline;

  private rubberBandId: string | null = null;
  private closeIndicatorId: string | null = null;
  private isWithinCloseThreshold: boolean = false;
  private measureLabels: MeasurementLabels | null = null;

  // ============== Entity Creation ==============

  /**
   * Create polyline entity with the first point.
   */
  protected createEntityWithFirstPoint(
    world: Point3D,
  ): DrawingModule.OdDbObjectId | null {
    const module = this.module;
    const db = this.viewer.database;

    if (!db) {
      return null;
    }

    // Create polyline object with one vertex
    const polyline = module.OdDbPolyline.createObject();
    polyline.setDatabaseDefaults(db, true);

    const point2d = new module.OdGePoint2d(world.x, world.y);
    polyline.addVertexAt(0, point2d, 0, 0, 0, 0);
    deleteObj(point2d);

    // Append to current space
    const currentSpaceId = getCurrentSpaceId(db);
    const currentSpace = openAs(
      currentSpaceId,
      module.OdDbBlockTableRecord,
      OpenMode.kForWrite,
    );
    const entityId = currentSpace.appendOdDbEntity(polyline);

    deleteObj(currentSpace);
    deleteObj(polyline);

    // Apply NoteConfig styling to the entity
    this.applyNoteConfigToEntity(entityId);

    // Execute AddEntityCommand to register for undo
    const command = new AddEntityCommand(this.viewer, {
      entityHandle: getEntityHandle(entityId),
      originalLayerName: this.viewer.activeNoteConfig.props.layername ?? "0",
    });
    this.viewer.executeCommand(command);
    trackShape("polyline");

    return entityId;
  }

  /**
   * Add a point to the existing polyline via command.
   */
  protected addPointToEntity(
    entityId: DrawingModule.OdDbObjectId,
    world: Point3D,
    vertexIndex: number,
  ): void {
    const command = new AddPointToPolylineCommand(this.viewer, {
      entityId,
      point: world,
      vertexIndex,
    });
    this.viewer.executeCommand(command);
    trackShape("polyline");
  }

  /**
   * Handle completion - set closed if requested and add hatch.
   */
  protected onComplete(closed: boolean, isAutoSelectEnabled = true): void {
    if (closed && this.currentEntityId) {
      const noteConfig = this.viewer.activeNoteConfig;
      // Use compound command that handles both closing and hatch creation
      const command = new ClosePolylineWithHatchCommand(this.viewer, {
        entityId: this.currentEntityId,
        hatchConfig: noteConfig.hatch,
        hatchProps: noteConfig.props,
      });
      this.viewer.executeCommand(command);
      trackShape("polyline");
    }

    // Auto-select if enabled
    if (this.currentEntityId && isAutoSelectEnabled) {
      this.selectCreatedEntity(this.currentEntityId);
    }
  }

  // ============== Preview ==============

  protected updatePreviewWithPoints(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    // This rebuilds (or removes) the close indicator from scratch below, so
    // any stale "within threshold" flag from a previous drag (e.g. left
    // over after an undo, which doesn't go through updatePreview()) must
    // not leak into the next preview cycle.
    this.isWithinCloseThreshold = false;

    // Clear close indicator
    if (this.closeIndicatorId) {
      overlay.remove(this.closeIndicatorId);
      this.closeIndicatorId = null;
    }

    // Clear rubber band
    if (this.rubberBandId) {
      overlay.remove(this.rubberBandId);
      this.rubberBandId = null;
    }

    // Clear measurement labels (they reappear on next drag in updatePreview)
    this.measureLabels?.clear();

    // Show close indicator if we have enough points to close
    if (this.worldPoints.length >= 3) {
      // Convert first world point to current screen coordinates (handles zoom/pan)
      const startWorld = this.worldPoints[0];
      const startScreen = this.viewer.worldToScreen(startWorld);
      this.closeIndicatorId = overlay.createCircle(
        startScreen.x,
        startScreen.y,
        15,
        PREVIEW_COMPLETION_STYLE,
      );
    }
  }

  protected updatePreview(screen: Point2D, exactDelta?: SegmentDelta): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || this.worldPoints.length === 0) return;

    // Convert last world point to current screen coordinates (handles zoom/pan)
    const lastWorld = this.worldPoints[this.worldPoints.length - 1];
    const lastScreen = this.viewer.worldToScreen(lastWorld);

    // Update rubber band
    if (this.rubberBandId) {
      overlay.update(this.rubberBandId, {
        x1: lastScreen.x,
        y1: lastScreen.y,
        x2: screen.x,
        y2: screen.y,
      });
    } else {
      this.rubberBandId = overlay.createLine(
        lastScreen.x,
        lastScreen.y,
        screen.x,
        screen.y,
        PREVIEW_RUBBER_BAND_STYLE,
      );
      if (this.rubberBandId) {
        this.addPreviewId(this.rubberBandId);
      }
    }

    // Update close indicator position and style based on proximity
    if (this.worldPoints.length >= 3) {
      const startWorld = this.worldPoints[0];
      const startScreen = this.viewer.worldToScreen(startWorld);

      // Check if within close threshold
      const dx = screen.x - startScreen.x;
      const dy = screen.y - startScreen.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const withinThreshold = distance < CLOSE_THRESHOLD;

      // Update indicator style if threshold state changed
      if (withinThreshold !== this.isWithinCloseThreshold) {
        this.isWithinCloseThreshold = withinThreshold;

        // Remove old indicator and create new one with correct style
        if (this.closeIndicatorId) {
          overlay.remove(this.closeIndicatorId);
          this.closeIndicatorId = null;
        }

        const style = withinThreshold
          ? PREVIEW_WILL_CLOSE_STYLE
          : PREVIEW_COMPLETION_STYLE;
        this.closeIndicatorId = overlay.createCircle(
          startScreen.x,
          startScreen.y,
          15,
          style,
        );
      } else if (this.closeIndicatorId) {
        // Just update position
        overlay.update(this.closeIndicatorId, {
          cx: startScreen.x,
          cy: startScreen.y,
        });
      }
    }

    this.updateMeasurements(screen, exactDelta);
  }

  private updateMeasurements(screen: Point2D, exactDelta?: SegmentDelta): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || this.worldPoints.length === 0) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    const lastWorld = this.worldPoints[this.worldPoints.length - 1];
    const lastScreen = this.viewer.worldToScreen(lastWorld);

    const dx = screen.x - lastScreen.x;
    const dy = screen.y - lastScreen.y;
    const screenLen = Math.sqrt(dx * dx + dy * dy);

    if (!exactDelta && screenLen <= 1) {
      this.measureLabels.clear();
      this.setDraftSegment({
        origin: lastWorld,
        dx: 0,
        dy: 0,
        length: 0,
        angleDeg: 0,
      });
      return;
    }

    // For typed input, use the exact world-space delta the user entered
    // rather than re-deriving it via screenToWorld — the WASM matrix
    // inverse can introduce rounding that would silently corrupt the
    // typed value.
    let wdx: number;
    let wdy: number;
    if (exactDelta) {
      wdx = exactDelta.dx;
      wdy = exactDelta.dy;
    } else {
      const curWorld = this.viewer.screenToWorld(screen);
      wdx = curWorld.x - lastWorld.x;
      wdy = curWorld.y - lastWorld.y;
    }
    const segmentLen = Math.sqrt(wdx * wdx + wdy * wdy);
    const angleDeg =
      segmentLen > 0.0001 ? Math.atan2(wdy, wdx) * (180 / Math.PI) : 0;
    this.setDraftSegment({
      origin: lastWorld,
      dx: wdx,
      dy: wdy,
      length: segmentLen,
      angleDeg,
    });

    if (screenLen <= 1) {
      // Typed delta resolves to a sub-pixel screen distance (e.g. very
      // zoomed out) — skip drawing labels/arc to avoid a divide-by-zero
      // in the perpendicular-offset math below; the panel already
      // reflects the typed value via setDraftSegment above.
      this.measureLabels.clear();
      return;
    }

    // -- Segment distance label --
    // Perpendicular normal biased upward
    let nx = -dy / screenLen;
    let ny = dx / screenLen;
    if (ny > 0) {
      nx = -nx;
      ny = -ny;
    }

    const midX = (lastScreen.x + screen.x) / 2 + nx * 18;
    const midY = (lastScreen.y + screen.y) / 2 + ny * 18;

    this.measureLabels.setLabel(
      "segment",
      midX,
      midY,
      MeasurementLabels.formatDistance(segmentLen),
    );

    // -- Segment bearing arc + label --
    if (segmentLen > 0.0001) {
      this.updateBearingIndicator(lastScreen, wdx, wdy);
    } else {
      this.clearBearingIndicator();
    }
  }

  private updateBearingIndicator(
    origin: Point2D,
    wdx: number,
    wdy: number,
  ): void {
    if (!this.measureLabels) return;

    const worldAngle = Math.atan2(wdy, wdx);
    // Negate for screen (Y-up world → Y-down screen)
    const screenAngle = -worldAngle;
    const ARC_RADIUS = 30;
    const lineLen = ARC_RADIUS * 1.5;

    // Reference line along screen x-axis from last vertex
    this.measureLabels.setLine(
      "refLine",
      origin.x,
      origin.y,
      origin.x + lineLen,
      origin.y,
    );

    // Direction line from last vertex toward current position
    this.measureLabels.setLine(
      "dirLine",
      origin.x,
      origin.y,
      origin.x + Math.cos(screenAngle) * lineLen,
      origin.y + Math.sin(screenAngle) * lineLen,
    );

    // Arc from x-axis (0) to the screen angle
    this.measureLabels.setArc(
      "arc",
      origin.x,
      origin.y,
      ARC_RADIUS,
      0,
      screenAngle,
      worldAngle > 0,
    );

    // Bearing label beside the arc midpoint
    const bearing = worldAngle * (180 / Math.PI);
    //if (bearing < 0) bearing += 360;
    const midScreenAngle = screenAngle / 2;
    const labelDist = ARC_RADIUS + 16;
    this.measureLabels.setLabel(
      "bearing",
      origin.x + Math.cos(midScreenAngle) * labelDist,
      origin.y + Math.sin(midScreenAngle) * labelDist,
      MeasurementLabels.formatAngle(bearing),
    );
  }

  private clearBearingIndicator(): void {
    if (!this.measureLabels) return;
    this.measureLabels.removeLabel("bearing");
    this.measureLabels.removeGuide("refLine");
    this.measureLabels.removeGuide("dirLine");
    this.measureLabels.removeGuide("arc");
  }

  // ============== Cleanup ==============

  protected clearPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      if (this.rubberBandId) {
        overlay.remove(this.rubberBandId);
      }
      if (this.closeIndicatorId) {
        overlay.remove(this.closeIndicatorId);
      }
    }
    this.measureLabels?.clear();
    this.rubberBandId = null;
    this.closeIndicatorId = null;
    // Clear parent's previewIds array
    this.previewIds = [];
  }

  protected clear(): void {
    this.clearPreview();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.isWithinCloseThreshold = false;
  }
}
